/**
 * @component NavigationMenu
 * @description shadcn/ui 기반 컴포넌트. CSS 변수를 활용한 테마 시스템 적용.
 * Based on shadcn/ui + Figma design specification
 */
import { createContext, useContext, useState, forwardRef, ReactNode } from 'react';
import { cn } from '../lib/utils';
import { ChevronDown } from 'lucide-react';

export interface NavigationMenuProps {
  children: ReactNode;
  className?: string;
}

export interface NavigationMenuListProps {
  children: ReactNode;
  className?: string;
}

export interface NavigationMenuItemProps {
  children: ReactNode;
  className?: string;
}

export interface NavigationMenuTriggerProps {
  children: ReactNode;
  className?: string;
}

export interface NavigationMenuContentProps {
  children: ReactNode;
  className?: string;
}

export interface NavigationMenuLinkProps {
  href: string;
  children: ReactNode;
  active?: boolean;
  className?: string;
}

interface NavigationMenuContextValue {
  openItem: string | null;
  setOpenItem: (item: string | null) => void;
}

interface NavigationMenuItemContextValue {
  id: string;
  isOpen: boolean;
}

const NavigationMenuContext = createContext<NavigationMenuContextValue | null>(null);
const NavigationMenuItemContext = createContext<NavigationMenuItemContextValue | null>(null);

export function NavigationMenu({ children, className }: NavigationMenuProps) {
  const [openItem, setOpenItem] = useState<string | null>(null);

  return (
    <NavigationMenuContext.Provider value={{ openItem, setOpenItem }}>
      <nav className={cn('relative z-10', className)}>
        {children}
      </nav>
    </NavigationMenuContext.Provider>
  );
}

export function NavigationMenuList({ children, className }: NavigationMenuListProps) {
  return (
    <ul className={cn('flex items-center gap-1', className)}>
      {children}
    </ul>
  );
}

let itemIdCounter = 0;
export function NavigationMenuItem({ children, className }: NavigationMenuItemProps) {
  const context = useContext(NavigationMenuContext);
  const [id] = useState(() => `nav-item-${itemIdCounter++}`);
  const isOpen = context?.openItem === id;

  return (
    <NavigationMenuItemContext.Provider value={{ id, isOpen }}>
      <li
        className={cn('relative', className)}
        onMouseEnter={() => context?.setOpenItem(id)}
        onMouseLeave={() => context?.setOpenItem(null)}
      >
        {children}
      </li>
    </NavigationMenuItemContext.Provider>
  );
}

export function NavigationMenuTrigger({ children, className }: NavigationMenuTriggerProps) {
  const context = useContext(NavigationMenuItemContext);

  return (
    <button
      type="button"
      className={cn(
        'group inline-flex h-10 w-max items-center justify-center rounded-md px-4 py-2 text-sm font-medium transition-colors',
        'hover:bg-accent-1 hover:text-foreground focus:outline-none',
        context?.isOpen && 'bg-accent-1',
        className
      )}
    >
      {children}
      <ChevronDown
        className={cn(
          'ml-1 h-4 w-4 transition-transform duration-200',
          context?.isOpen && 'rotate-180'
        )}
      />
    </button>
  );
}

export function NavigationMenuContent({ children, className }: NavigationMenuContentProps) {
  const context = useContext(NavigationMenuItemContext);

  if (!context?.isOpen) return null;

  return (
    <div
      className={cn(
        'absolute left-0 top-full mt-1.5 w-max rounded-lg border border-accent-2 bg-card p-4 shadow-lg',
        'animate-in fade-in-0 zoom-in-95',
        className
      )}
    >
      {children}
    </div>
  );
}

export const NavigationMenuLink = forwardRef<HTMLAnchorElement, NavigationMenuLinkProps>(
  ({ href, children, active, className }, ref) => {
    return (
      <a
        ref={ref}
        href={href}
        className={cn(
          'block select-none rounded-md p-3 leading-none no-underline outline-none transition-colors',
          'hover:bg-accent-1 focus:bg-accent-1',
          active && 'bg-accent-1',
          className
        )}
      >
        {children}
      </a>
    );
  }
);
NavigationMenuLink.displayName = 'NavigationMenuLink';
